/**
 * Next Store Modal - Modal for creating/editing next stores
 */
import { useState, useEffect } from 'react'
import { Button } from '@trafiflow/shared-ui'
import apiClient from '../../../shared/lib/api'
import type { NextStore, CreateNextStoreRequest, UpdateNextStoreRequest } from '../types'

interface NextStoreModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  store: NextStore | null
}

export default function NextStoreModal({
  isOpen,
  onClose,
  onSave,
  store,
}: NextStoreModalProps) {
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [description, setDescription] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setName(store?.name || '')
      setUrl(store?.url || '')
      setDescription(store?.description || '')
      setLogoUrl(store?.logoUrl || '')
      setError(null)
    }
  }, [isOpen, store])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!url.trim()) {
      setError('URL is required')
      return
    }

    // Validate URL format
    try {
      new URL(url.trim())
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)')
      return
    }

    try {
      setLoading(true)

      if (store) {
        // Update
        const updateData: UpdateNextStoreRequest = {
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || undefined,
          logoUrl: logoUrl.trim() || undefined,
        }
        await apiClient.put(`/next-stores/${store.id}`, updateData)
      } else {
        // Create
        const createData: CreateNextStoreRequest = {
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || undefined,
          logoUrl: logoUrl.trim() || undefined,
        }
        await apiClient.post('/next-stores', createData)
      }

      onSave()
    } catch (err: unknown) {
      console.error('Error saving next store:', err)
      const error = err as { response?: { status?: number; data?: { message?: string; error?: string } | string }; message?: string }
      
      let errorMessage = 'Failed to save store'
      if (error.response?.data) {
        if (typeof error.response.data === 'object' && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (typeof error.response.data === 'object' && error.response.data.error) {
          errorMessage = error.response.data.error
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      if (error.response?.status === 404) {
        errorMessage = 'Store endpoint not found. Please ensure the API is deployed.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-tf-bg-card rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-tf-text mb-4">
            {store ? 'Edit Store' : 'Add Store'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-tf-text-sub mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Store name"
                className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                disabled={loading}
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="url" className="block text-sm font-medium text-tf-text-sub mb-2">
                URL *
              </label>
              <input
                type="url"
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                disabled={loading}
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="description" className="block text-sm font-medium text-tf-text-sub mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Store description"
                rows={3}
                className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                disabled={loading}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="logoUrl" className="block text-sm font-medium text-tf-text-sub mb-2">
                Logo URL
              </label>
              <input
                type="url"
                id="logoUrl"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="mb-4 bg-tf-feedback-error-bg border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : store ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}


